// The chart section of the page: symbol picker, the SVG (price panel and gap panel, frozen windows, hover
// crosshair), the readout, the live status line and the freeze summary. Renders the same DOM as index.html
// (ids chart-pick, chart-wrap, chart, chart-readout, chart-live, chart-summary) so styles.css applies unchanged.
// The payload comes in as a prop; only the 60 s live extension (Chainlink round, DexScreener price) polls here.
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from "react";
import type { ChartPayload } from "../api/types";
import { buildScene, type Scene } from "./draw";
import { fmtEt, fmtPct, fmtPrice, fmtUtc } from "./format";
import { gapClass, hitAt, readoutAge, readoutPosition, readoutSource } from "./hover";
import { svgSize } from "./layout";
import { shortName } from "./more";
import { MorePicker } from "./MorePicker";
import { createRpcClient, defaultRpcProxy, LIVE_EVERY_MS, liveRow, pollLive } from "./live";
import { buildSeries, freezeSummary, seriesFor } from "./series";
import { appendLive, clipAnimation, clipEase, DEFAULT_SYMBOL, drawModeFor, pickerButtons, pruneLive, resolveSymbol, statusText, type DrawSnapshot, type LiveRows } from "./state";
import type { DataMode, HoverHit, LiveStatus, SeriesRow, View } from "./types";

export interface ChartProps {
  payload: ChartPayload | null;
  /** The symbol to show. The picker changes it and reports through onSymbolChange. Default "NVDA". */
  symbol?: string;
  onSymbolChange?: (symbol: string) => void;
  /** Where the payload came from: "live" (worker API), "recorded" (static file, API unreachable), "pending". */
  mode?: DataMode;
  /** Poll Chainlink and DexScreener every 60 s and extend the lines. Default true. */
  live?: boolean;
  /** JSON-RPC proxy tried first. Undefined: /api/rpc except on localhost. Null: no proxy. */
  rpcProxy?: string | null;
  /** Public JSON-RPC endpoints tried after the proxy. */
  rpcs?: string[];
  /** Rendered between #chart-live and #chart-summary (index.html has the API state line there). */
  children?: ReactNode;
  /**
   * Render one part only: the picker (#chart-pick), the chart (#chart-wrap and #chart-live) or the summary
   * (#chart-summary), so a parent can lay the parts out itself. Each part works from the props alone; the live
   * poller and the size observer run in the chart part. Without a slot the whole section is rendered.
   */
  slot?: ChartSlot;
}

export type ChartSlot = "picker" | "chart" | "summary";

const SVG_LABEL = "Chart: the oracle price is flat from Friday close; the pool price drifts above it through the weekend.";
const NO_ROWS: SeriesRow[] = [];
const NO_STATUS: LiveStatus = { ok: null, at: 0, err: "" };
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface Hover {
  hit: HoverHit;
  view: View;
  clientX: number;
  clientY: number;
}

export function Chart({ payload, symbol = DEFAULT_SYMBOL, onSymbolChange, mode = "pending", live: liveEnabled = true, rpcProxy, rpcs, children, slot }: ChartProps) {
  const drawsChart = slot === undefined || slot === "chart";
  // Symbol: the prop wins whenever it changes; the picker changes the local copy in between.
  const [picked, setPicked] = useState(symbol);
  const [prevSymbolProp, setPrevSymbolProp] = useState(symbol);
  if (symbol !== prevSymbolProp) {
    setPrevSymbolProp(symbol);
    setPicked(symbol);
  }
  const current = resolveSymbol(payload, picked);

  const [size, setSize] = useState<{ cw: number; ch: number } | null>(null);
  const [liveRows, setLiveRows] = useState<LiveRows>({});
  const [liveStatus, setLiveStatus] = useState<LiveStatus>(NO_STATUS);
  const [hover, setHover] = useState<Hover | null>(null);

  // A replaced payload: drop the live rows the recorded series now covers, and symbols it no longer has.
  const [prevPayload, setPrevPayload] = useState(payload);
  if (payload !== prevPayload) {
    setPrevPayload(payload);
    if (payload) {
      const lastRecorded = (sym: string) => {
        const e = payload.symbols[sym];
        return e ? (buildSeries(e).at(-1)?.t ?? 0) : 0;
      };
      setLiveRows((l) => pruneLive(l, payload, lastRecorded));
    }
  }

  const ext = current ? (liveRows[current] ?? NO_ROWS) : NO_ROWS;
  const entry = payload && current ? (payload.symbols[current] ?? null) : null;
  const series = useMemo(() => (entry ? seriesFor(entry, ext) : null), [entry, ext]);
  const dims = size ? svgSize(size.cw, size.ch) : null;
  const W = dims ? dims.W : 0, H = dims ? dims.H : 0;
  const scene = useMemo(
    () => (payload && current && series && W > 0 ? buildScene(payload, current, series, W, H) : null),
    [payload, current, series, W, H],
  );
  const summary = useMemo(() => (payload && current ? freezeSummary(payload, current) : ""), [payload, current]);
  const status = series ? statusText(series, mode, liveStatus) : { text: "", className: "inst-live" };
  const picker = payload && current ? pickerButtons(payload, current) : null;
  // A hover from an earlier draw is stale: hide it until the pointer moves again.
  const shown = hover && hover.view === scene?.view ? hover : null;

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const outRef = useRef<HTMLDivElement | null>(null);
  const clipRef = useRef<SVGRectElement | null>(null);
  const xhRef = useRef<SVGGElement | null>(null);
  const clipState = useRef({ w: 0, raf: 0 });
  const prevDraw = useRef<DrawSnapshot | null>(null);
  const payloadRef = useRef<ChartPayload | null>(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancelAnimationFrame(clipState.current.raf);
    };
  }, []);
  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);

  // Size: the SVG's client box, measured when #chart-wrap resizes (the first measurement at once, later ones
  // 120 ms after the last change, as chart.js did).
  useEffect(() => {
    const wrap = wrapRef.current, svg = svgRef.current;
    if (!wrap || !svg) return;
    const measure = () => {
      setSize((prev) => {
        const cw = svg.clientWidth, ch = svg.clientHeight;
        return prev?.cw === cw && prev.ch === ch ? prev : { cw, ch };
      });
    };
    let timer = 0, first = true;
    const onResize = () => {
      if (first) {
        first = false;
        measure();
        return;
      }
      setHover(null);
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 120);
    };
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(onResize);
      ro.observe(wrap);
      return () => {
        ro.disconnect();
        window.clearTimeout(timer);
      };
    }
    timer = window.setTimeout(onResize, 0);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(timer);
    };
  }, []);

  // Draw-in: the clip rectangle widens from the left (first paint, symbol switch) or from the last edge (growth).
  useLayoutEffect(() => {
    const snap: DrawSnapshot = { payload, symbol: current, mode, W, H, liveCount: ext.length, hasScene: scene !== null };
    const drawMode = drawModeFor(prevDraw.current, snap);
    prevDraw.current = snap;
    const rect = clipRef.current;
    if (!drawMode || !scene?.lastMark || !rect) return;
    const clip = clipState.current;
    cancelAnimationFrame(clip.raf);
    const { from, ms } = clipAnimation(drawMode, clip.w, W);
    if (!ms || reducedMotion() || from >= W) {
      rect.setAttribute("width", String(W));
      clip.w = W;
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const u = Math.min(1, (now - start) / ms);
      const w = from + (W - from) * clipEase(u);
      rect.setAttribute("width", w.toFixed(1));
      clip.w = w;
      if (u < 1) clip.raf = requestAnimationFrame(tick);
    };
    clip.raf = requestAnimationFrame(tick);
  }, [scene, payload, current, mode, W, H, ext.length]);

  // Hover: show or hide the crosshair group and place the readout next to the pointer.
  useLayoutEffect(() => {
    xhRef.current?.toggleAttribute("hidden", !shown);
    const out = outRef.current, wrap = wrapRef.current;
    if (!shown || !out || !wrap) return;
    const pos = readoutPosition(shown.clientX, shown.clientY, wrap.getBoundingClientRect(), { width: out.offsetWidth, height: out.offsetHeight });
    out.style.left = `${pos.left}px`;
    out.style.top = `${pos.top}px`;
  }, [shown]);

  // Live extension: poll at once and every 60 s for the shown symbol, restarted when it changes.
  const client = useMemo(() => createRpcClient({ proxy: defaultRpcProxy(window.location.hostname, rpcProxy), rpcs }), [rpcProxy, rpcs]);
  const hasPayload = payload !== null;
  useEffect(() => {
    if (!drawsChart || !liveEnabled || !hasPayload || !current) return;
    const sym = current;
    const poll = async () => {
      const e = payloadRef.current?.symbols[sym];
      if (!e) return;
      const reading = await pollLive(client, sym, e);
      if (!mounted.current) return;
      const row = liveRow(reading);
      if (row) {
        setLiveRows((l) => appendLive(l, sym, row));
        setLiveStatus({ ok: true, at: reading.at, err: "" });
      } else {
        setLiveStatus({ ok: false, at: reading.at, err: reading.err });
      }
      document.dispatchEvent(new CustomEvent("bellguard:live", { detail: { symbol: sym, feed: reading.feed, pool: reading.pool, at: reading.at } }));
    };
    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, LIVE_EVERY_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [drawsChart, liveEnabled, hasPayload, current, client]);

  const select = (sym: string) => {
    if (!payload?.symbols[sym]) return;
    setPicked(sym);
    setHover(null);
    onSymbolChange?.(sym);
    document.dispatchEvent(new CustomEvent("bellguard:symbol", { detail: { symbol: sym } }));
  };
  const onPointerMove = (ev: ReactPointerEvent<SVGSVGElement>) => {
    if (!scene) return;
    const hit = hitAt(scene.view, ev.clientX, ev.currentTarget.getBoundingClientRect());
    if (!hit) return;
    setHover({ hit, view: scene.view, clientX: ev.clientX, clientY: ev.clientY });
  };
  const onPointerLeave = () => {
    setHover(null);
  };

  const view = scene?.view ?? null;
  const r = shown?.hit.row ?? null;
  const pickerEl = (
    <div className="inst-pick" id="chart-pick" role="group" aria-label="Choose a Stock Token">
      {picker && (
        <>
          {picker.buttons.map((b) => (
            <button key={b.sym} type="button" className={b.on ? "cpick is-on" : "cpick"} data-sym={b.sym} title={b.title} onClick={() => { select(b.sym); }}>
              {b.sym}
            </button>
          ))}
          {picker.more.length > 0 && (
            <MorePicker
              items={picker.more.map((s) => ({ sym: s, name: shortName(payload?.symbols[s]?.name, s) }))}
              selected={picker.moreValue || null}
              onPick={select}
            />
          )}
        </>
      )}
    </div>
  );
  const chartEl = (
    <>
      <div className="chart-wrap" id="chart-wrap" ref={wrapRef}>
        <svg
          id="chart" className="chart" ref={svgRef}
          viewBox={W > 0 ? `0 0 ${W} ${H}` : "0 0 1200 420"} preserveAspectRatio="none" role="img" aria-label={SVG_LABEL}
          onPointerMove={onPointerMove} onPointerLeave={onPointerLeave}
        >
          {scene && view && (
            <>
              <SceneLayers scene={scene} clipRef={clipRef} />
              <g id="xhair" className="xhair" ref={xhRef}>
                <line id="xh-line" x1={shown?.hit.cx ?? 0} y1={view.mainTop} x2={shown?.hit.cx ?? 0} y2={view.gapBot} />
                <circle id="xh-pool" r={shown && shown.hit.pool == null ? 0 : 4} cx={shown?.hit.cx} cy={shown?.hit.pool ?? undefined} />
                <circle id="xh-oracle" r={shown && shown.hit.oracle == null ? 0 : 3.5} cx={shown?.hit.cx} cy={shown?.hit.oracle ?? undefined} />
                <circle id="xh-gap" r={shown && shown.hit.gap == null ? 0 : 3.5} cx={shown?.hit.cx} cy={shown?.hit.gap ?? undefined} />
              </g>
              <rect id="hover" x={view.padL} y={view.mainTop} width={W - view.padL - view.padR} height={view.gapBot - view.mainTop} fill="transparent" />
            </>
          )}
        </svg>
        <div className="chart-readout" id="chart-readout" ref={outRef} hidden={!r}>
          {r && (
            <>
              <div className="ro-time">{fmtUtc(r.t)}<span>{fmtEt(r.t)}</span></div>
              <div><span className="ro-k">Feed</span>{fmtPrice(r.oracle)}<span className="ro-s">round {r.updatedAt != null ? fmtUtc(r.updatedAt) : "n/a"}, age {readoutAge(r)}</span></div>
              <div><span className="ro-k">Pool</span>{fmtPrice(r.pool)}<span className="ro-s">{readoutSource(r.src)}</span></div>
              <div><span className="ro-k">Gap</span><b className={gapClass(r.basis)}>{fmtPct(r.basis)}</b></div>
            </>
          )}
        </div>
      </div>
      <p className={status.className} id="chart-live">{status.text}</p>
    </>
  );
  const summaryEl = <p className="inst-summary" id="chart-summary">{summary}</p>;
  if (slot === "picker") return pickerEl;
  if (slot === "chart") return chartEl;
  if (slot === "summary") return summaryEl;
  return (
    <section className="instrument" aria-labelledby="inst-title">
      <div className="inst-head">
        <h2 id="inst-title" className="sr-only">Pool price against the frozen oracle</h2>
        {pickerEl}
        <p className="inst-legend">
          <span className="key oracle"></span>Chainlink feed <span className="key pool"></span>Uniswap pool <span className="key gap"></span>the gap <span className="key frozen"></span>feed frozen <span className="key cash"></span>NYSE cash session
        </p>
      </div>
      {chartEl}
      {children}
      {summaryEl}
      <p className="inst-note" id="inst-note">Pool line: the basis monitor's reading of the largest pool for the token at each sample, about every 6.5 minutes, from Uniswap slot0 or DexScreener; the dashed prefix, where present, is GeckoTerminal's hourly close of the same pool before the monitor started. Feed line: every Chainlink round, stepped at its on-chain updatedAt. While the page is open the lines grow every 60 seconds from Chainlink (latestRoundData) and DexScreener; if a reading fails the chart keeps the recorded series and says so. Shaded: the feed's frozen windows from its session rule (Friday 20:00 ET to Sunday 20:00 ET, and market holidays); lighter: the NYSE cash session. Hover for the readings at any sample.</p>
    </section>
  );
}

interface LayersProps {
  scene: Scene;
  clipRef: RefObject<SVGRectElement | null>;
}

/** The static part of a draw: defs, shading, grid, labels, markers and the clipped line layers. */
const SceneLayers = memo(function SceneLayers({ scene, clipRef }: LayersProps) {
  const { W, H } = scene.view;
  const L = scene.layers, m = scene.lastMark;
  return (
    <>
      <defs>
        <pattern id="hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="8" /></pattern>
        <filter id="cglow" x="-10%" y="-50%" width="120%" height="200%"><feGaussianBlur in="SourceGraphic" stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        <clipPath id="chart-clip"><rect id="clip-rect" ref={clipRef} x="0" y="0" width={W} height={H} /></clipPath>
      </defs>
      {scene.decor.map((it, i) => {
        switch (it.kind) {
          case "rect": return <rect key={i} className={it.cls} x={it.x} y={it.y} width={it.width} height={it.height} />;
          case "line": return <line key={i} className={it.cls} x1={it.x1} y1={it.y1} x2={it.x2} y2={it.y2} />;
          case "text": return <text key={i} className={it.cls} x={it.x} y={it.y} textAnchor={it.anchor}>{it.text}</text>;
          case "path": return <path key={i} className={it.cls} d={it.d} />;
        }
      })}
      <g clipPath="url(#chart-clip)">
        <path className="gapfill" d={L.gapFill} />
        <path className="gapline" d={L.gapLine} />
        <path className="oracle" d={L.oracle} />
        <path className="pool hourly" d={L.hourly} />
        <path className="pool" d={L.monitor} />
        <path className="pool connector" d={L.connector} />
        <path className="pool live" d={L.live} />
        {m && (
          <>
            <circle className="marker" cx={m.cx} cy={m.cy} r="4" />
            <text className="label neon" x={m.labelX} y={m.labelY} textAnchor={m.anchor}>{m.text}</text>
          </>
        )}
      </g>
    </>
  );
});
